
import jsPDF from "jspdf";
import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, Header, Footer, AlignmentType, HeadingLevel, WidthType, BorderStyle, ImageRun } from 'docx';
import { saveAs } from 'file-saver';
import html2canvas from "html2canvas";
import logoBase64 from "@assets/inspections1.png";
import reportimage from "@assets/reportimg.png";

import { message } from "antd";
import { Buffer } from 'buffer';
import logoBase64docx from "@assets/inspections1.png?inline";
import toast from "react-hot-toast";

// Professional maritime inspection design system
const designSystem = {
    colors: {
        primary: {
            50: [245, 251, 248],
            100: [229, 245, 238],
            500: [35, 138, 112],   // #238A70
            600: [28, 110, 90],
            900: [18, 69, 56]
        },
        gray: {
            50: [249, 250, 251],
            100: [243, 244, 246],
            200: [229, 231, 235],
            300: [209, 213, 219],
            400: [156, 163, 175],
            500: [107, 114, 128],
            600: [75, 85, 99],
            700: [55, 65, 81],
            800: [31, 41, 55],
            900: [17, 24, 39]
        },
        white: [255, 255, 255],
        black: [0, 0, 0]
    },

    fontSize: {
        xs: 8, sm: 9, base: 10, lg: 11, xl: 12, '2xl': 14, '3xl': 16, '4xl': 18, '5xl': 20, '6xl': 22
    },

    spacing: {
        0: 0, 1: 1, 2: 2, 3: 3, 4: 4, 5: 5, 6: 6, 8: 8, 10: 10, 12: 12, 16: 16, 20: 20, 24: 24
    }
};



// PDF Utilities
const pdfUtils = {
    text: {
        xs: (pdf) => pdf.setFontSize(designSystem.fontSize.xs),
        sm: (pdf) => pdf.setFontSize(designSystem.fontSize.sm),
        base: (pdf) => pdf.setFontSize(designSystem.fontSize.base),
        lg: (pdf) => pdf.setFontSize(designSystem.fontSize.lg),
        xl: (pdf) => pdf.setFontSize(designSystem.fontSize.xl),
        '2xl': (pdf) => pdf.setFontSize(designSystem.fontSize['2xl']),
        '3xl': (pdf) => pdf.setFontSize(designSystem.fontSize['3xl']),
        '4xl': (pdf) => pdf.setFontSize(designSystem.fontSize['4xl']),
        '5xl': (pdf) => pdf.setFontSize(designSystem.fontSize['5xl']),
        '6xl': (pdf) => pdf.setFontSize(designSystem.fontSize['6xl'])
    },

    font: {
        normal: (pdf) => pdf.setFont(undefined, 'normal'),
        bold: (pdf) => pdf.setFont(undefined, 'bold'),
        italic: (pdf) => pdf.setFont(undefined, 'italic')
    },

    textColor: {
        primary: (pdf) => pdf.setTextColor(...designSystem.colors.primary[500]),
        'primary-dark': (pdf) => pdf.setTextColor(...designSystem.colors.primary[900]),
        'gray-400': (pdf) => pdf.setTextColor(...designSystem.colors.gray[400]),
        'gray-500': (pdf) => pdf.setTextColor(...designSystem.colors.gray[500]),
        'gray-600': (pdf) => pdf.setTextColor(...designSystem.colors.gray[600]),
        'gray-700': (pdf) => pdf.setTextColor(...designSystem.colors.gray[700]),
        'gray-800': (pdf) => pdf.setTextColor(...designSystem.colors.gray[800]),
        'gray-900': (pdf) => pdf.setTextColor(...designSystem.colors.gray[900]),
        white: (pdf) => pdf.setTextColor(...designSystem.colors.white),
        black: (pdf) => pdf.setTextColor(...designSystem.colors.black)
    },

    bg: {
        primary: (pdf) => pdf.setFillColor(...designSystem.colors.primary[500]),
        'primary-50': (pdf) => pdf.setFillColor(...designSystem.colors.primary[50]),
        'primary-100': (pdf) => pdf.setFillColor(...designSystem.colors.primary[100]),
        'gray-50': (pdf) => pdf.setFillColor(...designSystem.colors.gray[50]),
        'gray-100': (pdf) => pdf.setFillColor(...designSystem.colors.gray[100]),
        'gray-200': (pdf) => pdf.setFillColor(...designSystem.colors.gray[200]),
        'gray-800': (pdf) => pdf.setFillColor(...designSystem.colors.gray[800]),
        white: (pdf) => pdf.setFillColor(...designSystem.colors.white)
    },

    border: {
        'gray-200': (pdf) => pdf.setDrawColor(...designSystem.colors.gray[200]),
        'gray-300': (pdf) => pdf.setDrawColor(...designSystem.colors.gray[300]),
        primary: (pdf) => pdf.setDrawColor(...designSystem.colors.primary[500])
    }
};
// // PDF Components
const pdfComponents = {
    header: (pdf, x, y, width, height, title, subtitle, inspection, shipinfo) => {
        const logoWidth = 12;
        const logoHeight = 12;
        const logoY = y;

        if (logoBase64) {
            pdf.addImage(logoBase64, "PNG", x, logoY, logoWidth, logoHeight);
        }

        const spotFlashX = x + logoWidth + 2;
        const spotFlashY = logoY + logoHeight / 2 + 2;

        pdf.setFontSize(10);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(0, 135, 175);
        pdf.text('Spot', spotFlashX, spotFlashY, { align: 'left' });

        const spotWidth = pdf.getTextWidth('Spot');
        pdf.setTextColor(0, 174, 104);
        pdf.text('Flash', spotFlashX + spotWidth, spotFlashY, { align: 'left' });

        const centerX = x + width / 2;
        const titleY = y + 8;
        pdf.setFontSize(12);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(60, 60, 60);
        pdf.text(title, centerX, titleY, { align: 'center' });

        const rightX = x + width - 5;
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(60, 60, 60);

        const dateTime = `Date & Time: ${new Date().toLocaleDateString('en-GB')} ${new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
        const imo = `IMO: ${shipinfo?.imo_number || 'N/A'}`;

        pdf.text(dateTime, rightX, y + 5, { align: 'right' });
        pdf.text(imo, rightX, y + 12, { align: 'right' });
    },

    moduleHeader: (pdf, x, y, width, moduleName, moduleGuidance) => {
        let currentY = y;

        pdf.setFontSize(10);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(0, 120, 120);

        const capitalizedName = moduleName.replace(/\b\w/g, l => l.toUpperCase());
        pdf.text(capitalizedName, x, currentY);
        currentY += 10;

        if (moduleGuidance) {
            pdf.setFontSize(10);
            pdf.setFont("helvetica", "normal");
            pdf.setTextColor(100, 100, 100);

            const guidanceLines = pdf.splitTextToSize(`Guidance: ${moduleGuidance}`, width - 10);
            pdf.text(guidanceLines, x + 5, currentY);
            currentY += (guidanceLines.length * 3.5) + 5;
        }

        return currentY;
    },

    unitHeader: (pdf, x, y, width, stepName, unitIndex) => {
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(0, 120, 120);
        const unitNumber = `${stepName}.${unitIndex + 1}`;
        pdf.text(unitNumber, x, y);
        return y + 8;
    },

    horizontalSeparator: (pdf, x, y, width) => {
        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(0.3);
        pdf.line(x, y, x + width, y);
        return y + 5;
    },

    inspectionRemarks: (pdf, x, y, width, commonData, fieldDescriptions = {}) => {
        if (!commonData || !commonData.form_data || Object.keys(commonData.form_data).length === 0) {
            return y;
        }

        const commonEntries = Object.entries(commonData.form_data)
            .filter(([key, value]) => value && value.toString().trim() !== '');

        if (commonEntries.length === 0) {
            return y;
        }

        let currentY = y;

        pdf.setFontSize(10);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(0, 120, 120);
        pdf.text('REMARKS', x, currentY);
        currentY += 8;

        let itemNumber = 1;
        const descriptionWidth = width * 0.65;
        const valueStartX = x + descriptionWidth + 10;

        for (let [key, value] of commonEntries) {
            const formattedKey = key.replace(/_/g, ' ')
                .replace(/([A-Z])/g, ' $1')
                .replace(/^\w/, c => c.toUpperCase())
                .trim();

            const fieldDescription = fieldDescriptions[key] || fieldDescriptions[key.toLowerCase()] || 'Additional information';

            pdf.setFontSize(10);
            pdf.setFont("helvetica", "bold");
            pdf.setTextColor(60, 60, 60);
            const itemNumberText = `R.${itemNumber}`;
            pdf.text(itemNumberText, x, currentY);

            pdf.setFontSize(10);
            pdf.setFont("helvetica", "normal");
            pdf.setTextColor(60, 60, 60);

            const fullDescription = `${formattedKey}: ${fieldDescription}`;
            const descLines = pdf.splitTextToSize(fullDescription, descriptionWidth - 30);
            pdf.text(descLines, x + 20, currentY);

            pdf.setFontSize(10);
            pdf.setFont("helvetica", "normal");
            pdf.setTextColor(0, 0, 0);
            const valueText = value.toString();
            const valueLines = pdf.splitTextToSize(valueText, width - descriptionWidth - 20);
            pdf.text(valueLines, valueStartX, currentY);

            const maxLines = Math.max(descLines.length, valueLines.length);
            currentY += (maxLines * 3.5) + 5;

            if (itemNumber < commonEntries.length) {
                pdf.setDrawColor(245, 245, 245);
                pdf.setLineWidth(0.3);
                pdf.line(x, currentY - 2, x + width, currentY - 2);
            }

            itemNumber++;
        }

        return currentY + 5;
    },

    measurementDataSection: (pdf, x, y, width, formData, fieldDescriptions = {}) => {
        const formEntries = Object.entries(formData)
            .filter(([key, value]) => value && value.toString().trim() !== '');

        let dataY = y;

        pdf.setFontSize(10);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(0, 120, 120);
        pdf.text('Data Points', x, dataY);
        dataY += 8;

        const descriptionWidth = width * 0.55;
        const valueStartX = x + descriptionWidth + 10;

        for (let [key, value] of formEntries) {
            const formattedKey = key.replace(/_/g, ' ')
                .replace(/([A-Z])/g, ' $1')
                .replace(/^\w/, c => c.toUpperCase())
                .trim();

            const fieldDescription = fieldDescriptions[key] || fieldDescriptions[key.toLowerCase()] || 'Additional information';

            pdf.setFontSize(10);
            pdf.setFont("helvetica", "normal");
            pdf.setTextColor(60, 60, 60);

            const fullDescription = `${formattedKey}: ${fieldDescription}`;
            const descLines = pdf.splitTextToSize(fullDescription, descriptionWidth - 10);
            pdf.text(descLines, x, dataY);

            pdf.setFontSize(10);
            pdf.setFont("helvetica", "normal");
            pdf.setTextColor(0, 0, 0);
            const valueText = value.toString();
            const valueLines = pdf.splitTextToSize(valueText, width - descriptionWidth - 20);
            pdf.text(valueLines, valueStartX, dataY);

            const maxLines = Math.max(descLines.length, valueLines.length);
            dataY += (maxLines * 3.5) + 5;

            if (formEntries.indexOf([key, value]) < formEntries.length - 1) {
                pdf.setDrawColor(245, 245, 245);
                pdf.setLineWidth(0.3);
                pdf.line(x, dataY - 2, x + width, dataY - 2);
            }
        }

        return dataY + 3;
    },

    imageSection: (pdf, x, y, width, images, isFullWidth = false) => {
        if (!images || images.length === 0) return y;

        let currentY = y;
        const imageSize = isFullWidth ? 45 : 38;
        const imageSpacing = isFullWidth ? 50 : 43;
        const imagesPerRow = Math.floor(width / imageSpacing);
        const imageNameHeight = 5;

        let imgX = x;
        let imgY = currentY;
        let imagesInRow = 0;

        for (let imgIndex = 0; imgIndex < images.length; imgIndex++) {
            const image = images[imgIndex];

            if (imagesInRow >= imagesPerRow) {
                imgX = x;
                imgY += imageSize + imageNameHeight + 6;
                imagesInRow = 0;
            }

            try {
                if (image.image_url) {
                    pdf.addImage(image.image_url, 'JPEG', imgX, imgY, imageSize, imageSize);

                    pdf.setFontSize(10);
                    pdf.setFont("helvetica", "normal");
                    pdf.setTextColor(80, 80, 80);
                    const imageName = image.component_name || `Image ${imgIndex + 1}`;
                    const truncatedName = imageName.length > 25 ? imageName.substring(0, 22) + '...' : imageName;
                    pdf.text(truncatedName, imgX + imageSize / 2, imgY + imageSize + 3, { align: 'center' });
                }
            } catch (error) {
                pdf.setFillColor(240, 240, 240);
                pdf.rect(imgX, imgY, imageSize, imageSize, 'F');

                pdf.setTextColor(160, 160, 160);
                pdf.setFontSize(10);
                pdf.setFont("helvetica", "normal");
                pdf.text('Image', imgX + imageSize / 2, imgY + imageSize / 2 - 2, { align: 'center' });
                pdf.text('Unavailable', imgX + imageSize / 2, imgY + imageSize / 2 + 2, { align: 'center' });
            }

            imgX += imageSpacing;
            imagesInRow++;
        }

        return imgY + imageSize + imageNameHeight + 6;
    },

    // NEW: Combined ship and user details section
    drawShipAndUserDetails: (pdf, x, y, width, shipinfo, userinfo) => {
        let currentY = y;
 
        // Vessel name and IMO - larger subtitle
        pdf.setFontSize(12);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(70, 85, 120);
        const vesselText = `${shipinfo?.name || 'UNKNOWN VESSEL'}  ${shipinfo?.imo_number || 'N/A'}`;
        pdf.text(vesselText.toUpperCase(), x, currentY);

        // Create two-column layout for ship and user info
        const columnWidth = (width - 20) / 2;
        const leftColumnX = x;
        const rightColumnX = x + columnWidth + 20;

      // SHIP INFORMATION SECTION (Left Column)
        let leftY = currentY + 15;
        
        // Ship info header with rounded green border (no background)
        const cornerRadius = 2;
        pdf.setDrawColor(34, 139, 34); // Green color
        pdf.setLineWidth(0.3);
        pdf.roundedRect(leftColumnX, leftY - 5, columnWidth, 10, cornerRadius, cornerRadius);
        
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(34, 139, 34); // Green text color
        pdf.text('VESSEL INFORMATION', leftColumnX + 3, leftY + 1);
        leftY += 15;

        // Format year of build
        let yearBuilt = 'N/A';
        if (shipinfo?.year_of_build) {
            yearBuilt = new Date(shipinfo.year_of_build).getFullYear().toString();
        }

        const shipDetails = [
            { label: 'Vessel Name', value: shipinfo?.name || 'Unknown Vessel' },
            { label: 'IMO Number', value: shipinfo?.imo_number || 'N/A' },
            { label: 'Vessel Type', value: shipinfo?.vessel_type || 'N/A' },
            { label: 'Deadweight (DWT)', value: shipinfo?.deadweight || 'N/A' },
            { label: 'Gross Tonnage', value: shipinfo?.gross_tonnage || 'N/A' },
            { label: 'Engine Model', value: shipinfo?.engine_model || 'N/A' },
            { label: 'Year of Build', value: yearBuilt }
        ];

        shipDetails.forEach(detail => {
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(9);
            pdf.setTextColor(100, 100, 100);
            pdf.text(detail.label + ':', leftColumnX, leftY);
            leftY += 4;

            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(9);
            pdf.setTextColor(0, 0, 0);
            const valueLines = pdf.splitTextToSize(detail.value, columnWidth - 10);
            pdf.text(valueLines, leftColumnX + 3, leftY);
            leftY += (valueLines.length * 4) + 6;
        });

        // USER INFORMATION SECTION (Right Column)
        let rightY = currentY + 15;
        
        // User info header with rounded teal border (no background)
        pdf.setDrawColor(0, 128, 128); // Teal color
        pdf.setLineWidth(0.3);
        pdf.roundedRect(rightColumnX, rightY - 5, columnWidth, 10, cornerRadius, cornerRadius);
        
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(0, 128, 128); // Teal text color
        pdf.text('INSPECTOR INFORMATION', rightColumnX + 3, rightY + 1);
        rightY += 15;

        const userDetails = [
            { label: 'Inspector Name', value: userinfo?.username || 'N/A' },
            { label: 'Email Address', value: userinfo?.email || 'N/A' },
            { label: 'Designation', value: userinfo?.designation || 'N/A' },
            { label: 'Inspection Date', value: new Date().toLocaleDateString('en-GB') },
            { label: 'Report Generated', value: `${new Date().toLocaleDateString('en-GB')} ${new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` }
        ];

        userDetails.forEach(detail => {
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(9);
            pdf.setTextColor(100, 100, 100);
            pdf.text(detail.label + ':', rightColumnX, rightY);
            rightY += 4;

            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(9);
            pdf.setTextColor(0, 0, 0);
            const valueLines = pdf.splitTextToSize(detail.value, columnWidth - 10);
            pdf.text(valueLines, rightColumnX + 3, rightY);
            rightY += (valueLines.length * 4) + 6;
        });
        // Add a decorative separator line between sections
        const separatorY = Math.max(leftY, rightY) + 8;


        return separatorY + 10;
    },

    // Enhanced ship icon with better styling
    drawShipIcon: (pdf, x, y, width) => {
        const shipWidth = 100;
        const shipHeight = 70;
        const shipX = x + (width - shipWidth) / 2;
        const shipY = y;

        try {
            // Try to add the local image
            pdf.addImage(reportimage, 'PNG', shipX, shipY, shipWidth, shipHeight);
        } catch (error) {
            console.warn('Could not load reportimg.png, falling back to drawn ship icon:', error);

            // Fallback: Draw ship icon if image fails to load
            pdf.setFillColor(70, 130, 180);
            pdf.setDrawColor(50, 100, 150);
            pdf.setLineWidth(1.5);

            // Hull bottom curve
            pdf.ellipse(shipX + shipWidth / 2, shipY + shipHeight - 5, shipWidth * 0.8, 12, 'FD');

            // Hull main rectangle
            pdf.rect(shipX + 10, shipY + 15, shipWidth - 20, shipHeight - 25, 'FD');

            // Ship superstructure
            pdf.setFillColor(200, 200, 200);
            pdf.setDrawColor(150, 150, 150);
            pdf.rect(shipX + 20, shipY + 5, shipWidth - 40, 15, 'FD');

            // Bridge/wheelhouse
            pdf.setFillColor(180, 180, 180);
            pdf.setDrawColor(130, 130, 130);
            pdf.rect(shipX + 30, shipY, shipWidth - 60, 10, 'FD');

            // Mast
            pdf.setDrawColor(100, 100, 100);
            pdf.setLineWidth(2);
            pdf.line(shipX + shipWidth / 2, shipY, shipX + shipWidth / 2, shipY - 15);

            // Flag
            pdf.setFillColor(220, 50, 50);
            pdf.rect(shipX + shipWidth / 2 + 1, shipY - 12, 8, 5, 'F');

            // Add some port holes for detail
            pdf.setFillColor(50, 80, 120);
            for (let i = 0; i < 3; i++) {
                pdf.circle(shipX + 25 + (i * 15), shipY + 20, 2, 'F');
            }
        }

        return shipY + shipHeight + 20;
    }

};

// Helper function to get field descriptions from form configuration
const getFieldDescriptionsFromConfig = (formConfiguration) => {
    const descriptions = {};

    if (formConfiguration && Array.isArray(formConfiguration)) {
        formConfiguration.forEach(module => {
            // Add descriptions from non-common fields
            if (module.nonCommonFields) {
                module.nonCommonFields.forEach(field => {
                    descriptions[field.name] = field.guidance_text || 'Additional information';
                });
            }

            // Add descriptions from common fields
            if (module.commonFields) {
                module.commonFields.forEach(field => {
                    descriptions[field.name] = field.guidance_text || 'Additional information';
                });
            }
        });
    }

    return descriptions;
};

// Helper function to get module guidance from form configuration
const getModuleGuidance = (moduleName, formConfiguration) => {
    if (formConfiguration && Array.isArray(formConfiguration)) {
        const moduleConfig = formConfiguration.find(module =>
            module.name.toLowerCase() === moduleName.toLowerCase() ||
            module.tableName.toLowerCase() === moduleName.toLowerCase()
        );

        if (moduleConfig && moduleConfig.formMeta) {
            const guidance = moduleConfig.formMeta.guidance;
            const description = moduleConfig.formMeta.description;

            // Combine description and guidance
            let combinedGuidance = '';
            if (description) {
                combinedGuidance += description;
            }
            if (guidance && Array.isArray(guidance) && guidance.length > 0 && guidance[0]) {
                if (combinedGuidance) combinedGuidance += '. ';
                combinedGuidance += guidance[0];
            }

            return combinedGuidance || `Follow standard inspection procedures and document all findings accurately for ${moduleName.toLowerCase()}. Ensure all safety protocols are maintained.`;
        }
    }

    return `Follow standard inspection procedures and document all findings accurately for ${moduleName.toLowerCase()}. Ensure all safety protocols are maintained.`;
};

export const exportToPDF = async (inspection, shipinfo, userinfo, formconfiguration) => {

    if (!inspection || inspection.length === 0) {
        toast.error('No data to export');
        return;
    }

    try {
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 12;
        const contentWidth = pageWidth - 2 * margin;
        const footerHeight = 12;
        const maxContentHeight = pageHeight - footerHeight - 25;
        const borderMargin = 6;

        let yPosition = margin;

        // Get field descriptions from form configuration
        const fieldDescriptions = getFieldDescriptionsFromConfig(formconfiguration);

        const drawGreenBorder = () => {
            pdf.setDrawColor(34, 139, 34);
            pdf.setLineWidth(0.5);
            pdf.rect(borderMargin, borderMargin, pageWidth - (2 * borderMargin), pageHeight - (2 * borderMargin));
        };

        const drawHeader = () => {
            const headerHeight = 16;
            pdfComponents.header(
                pdf, margin, yPosition, contentWidth, headerHeight,
                'Inspection Report', '', inspection, shipinfo
            );
            return yPosition + headerHeight + 4;
        };

        const drawFooter = () => {
            pdf.setFillColor(34, 139, 34);
            pdf.rect(borderMargin, pageHeight - 14 - borderMargin, pageWidth - (2 * borderMargin), 14, 'F');

            pdf.setFontSize(10);
            pdf.setFont("helvetica", "normal");
            pdf.setTextColor(255, 255, 255);

            pdf.text('Edkas Software - Spot Flash Inspections', margin, pageHeight - 7 - borderMargin);
        };

        const addNewPage = () => {
            pdf.addPage();
            drawGreenBorder();
            yPosition = margin;
            return drawHeader();
        };

        const checkPageBreak = (requiredHeight) => {
            if (yPosition + requiredHeight > maxContentHeight) {
                yPosition = addNewPage();
            }
        };

        // FIRST PAGE WITH ENHANCED LAYOUT
        drawGreenBorder();
        yPosition = drawHeader();
        yPosition += 8;

        // Draw ship icon
        yPosition = pdfComponents.drawShipIcon(pdf, margin, yPosition, contentWidth);

        // Draw combined ship and user details
        yPosition = pdfComponents.drawShipAndUserDetails(pdf, margin, yPosition, contentWidth, shipinfo, userinfo);

        drawFooter();

        // SUBSEQUENT PAGES (Inspection Data)
         if (inspection.data && inspection.data.length > 0) {
            yPosition = addNewPage();

            for (let stepIndex = 0; stepIndex < inspection.data.length; stepIndex++) {
                const step = inspection.data[stepIndex];
                checkPageBreak(40);

                // Get module guidance from form configuration
                const moduleGuidance = getModuleGuidance(step.step_name, formconfiguration);
                yPosition = pdfComponents.moduleHeader(pdf, margin, yPosition, contentWidth, step.step_name, moduleGuidance);

                if (step.units && step.units.length > 0) {
                    // Process all units first
                    for (let unitIndex = 0; unitIndex < step.units.length; unitIndex++) {
                        const unit = step.units[unitIndex];
                        const hasImages = unit.images && unit.images.length > 0;
                        const hasFormData = unit.form_data && Object.keys(unit.form_data).length > 0;

                        if (!hasImages && !hasFormData) continue;

                        checkPageBreak(50);

                        yPosition = pdfComponents.unitHeader(pdf, margin, yPosition, contentWidth, step.step_name, unitIndex);

                        if (hasImages && hasFormData) {
                            const leftWidth = contentWidth * 0.5;
                            const rightWidth = contentWidth * 0.45;
                            const rightX = margin + leftWidth + 10;

                            const imageEndY = pdfComponents.imageSection(pdf, margin, yPosition, leftWidth, unit.images, false);
                            const dataEndY = pdfComponents.measurementDataSection(pdf, rightX, yPosition, rightWidth, unit.form_data, fieldDescriptions);

                            yPosition = Math.max(imageEndY, dataEndY) + 4;

                        } else if (hasImages) {
                            yPosition = pdfComponents.imageSection(pdf, margin, yPosition, contentWidth, unit.images, true);
                        } else if (hasFormData) {
                            yPosition = pdfComponents.measurementDataSection(pdf, margin, yPosition, contentWidth, unit.form_data, fieldDescriptions);
                        }

                        yPosition = pdfComponents.horizontalSeparator(pdf, margin, yPosition, contentWidth);
                    }

                    // Add module-level remarks ONCE after all units are processed
                    if (step.common_data && step.common_data.form_data) {
                        checkPageBreak(30);
                        yPosition = pdfComponents.inspectionRemarks(
                            pdf, margin, yPosition, contentWidth, step.common_data, fieldDescriptions
                        );
                    }
                }

                yPosition += 5;
            }
        }
        // Add page numbers
        const totalPages = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);

            drawGreenBorder();

            pdf.setFillColor(34, 139, 34);
            pdf.rect(borderMargin, pageHeight - 14 - borderMargin, pageWidth - (2 * borderMargin), 14, 'F');

            pdf.setFontSize(10);
            pdf.setFont("helvetica", "normal");
            pdf.setTextColor(255, 255, 255);

            pdf.text('Edkas Software - Spot Flash Inspections', margin, pageHeight - 7 - borderMargin);
            pdf.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 7 - borderMargin, { align: 'right' });
        }

        const fileName = `maritime_inspection_report_${shipinfo?.name || 'vessel'}_${new Date().toISOString().split('T')[0]}.pdf`;
        pdf.save(fileName);
        toast.success('Professional maritime inspection report exported successfully');

    } catch (error) {
        console.error('Error exporting PDF:', error);
        toast.error('Failed to export PDF: ' + error.message);
    }
};
export const exportToDOCX = async (inspection) => {
    if (!inspection || inspection.length === 0) {
        toast.error('No data to export');
        return;
    }
     try {
        const documentElements = [];

        // Add header - matching PDF header exactly
        const headerElements = await docxComponents.createHeader();
        documentElements.push(...headerElements);

        // Process inspection data
        for (const step of inspection.data) {
            // Add module section (Step1, Step2, etc.)
            const moduleElements = await docxComponents.createModuleSection(step);
            documentElements.push(...moduleElements);

            // Process units within each step
            if (step.units && step.units.length > 0) {
                for (const unit of step.units) {
                    const hasImages = unit.images && unit.images.length > 0;
                    const hasFormData = unit.form_data && Object.keys(unit.form_data).length > 0;

                    if (!hasImages && !hasFormData) continue;

                    // Add unit title
                    documentElements.push(
                        docxUtils.createParagraph(unit.unit_name, {
                            bold: true,
                            size: docxDesignSystem.sizes.heading2 * 2,
                            color: docxDesignSystem.colors.gray.dark,
                            heading: HeadingLevel.HEADING_2,
                            spaceAfter: 300
                        })
                    );

                    if (hasImages && hasFormData) {
                        // Create side-by-side layout exactly like PDF
                        const unitElements = await docxComponents.createSideBySideLayout(unit.images, unit.form_data);
                        documentElements.push(...unitElements);
                    } else if (hasImages) {
                        // Full width images
                        const imageElements = await docxComponents.createImagesContent(unit.images);
                        documentElements.push(...imageElements);
                    } else if (hasFormData) {
                        // Full width data
                        const dataElements = docxComponents.createDataContent(unit.form_data);
                        documentElements.push(...dataElements);
                    }
                }

                // Add remarks section if exists
                if (step.common_data && step.common_data.form_data) {
                    const remarksElements = docxComponents.createRemarksSection(step.common_data);
                    documentElements.push(...remarksElements);
                }
            }

            // Add spacing between modules
            documentElements.push(
                docxUtils.createParagraph("", { spaceAfter: 600 })
            );
        }

        // Create the document
        const doc = new Document({
            sections: [{
                properties: {
                    page: {
                        margin: {
                            top: 720,    // 0.5 inch
                            right: 720,  // 0.5 inch
                            bottom: 720, // 0.5 inch
                            left: 720    // 0.5 inch
                        }
                    }
                },

                footers: {
                    default: new Footer({
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: "Edkas Software - Spot Flash Inspection",
                                        size: docxDesignSystem.sizes.caption * 2,
                                        color: docxDesignSystem.colors.gray.medium
                                    })
                                ],
                                alignment: AlignmentType.LEFT
                            }),
                            // new Paragraph({
                            //     children: [
                            //         new TextRun({
                            //             text: "",
                            //             size: docxDesignSystem.sizes.caption * 2,
                            //             color: docxDesignSystem.colors.gray.medium
                            //         })
                            //     ],
                            //     alignment: AlignmentType.RIGHT
                            // })
                        ]
                    })
                },
                children: documentElements
            }]
        });

        const fileName = `maritime_inspection_report_${new Date().toISOString().split('T')[0]}.docx`;
        const blob = await Packer.toBlob(doc);
        saveAs(blob, fileName);
        toast.success('Professional maritime inspection report exported as Word document successfully');

    } catch (error) {
        console.error('Error exporting DOCX:', error);
        toast.error('Failed to export Word document: ' + error.message);
    }
};

// Fixed DOCX Components
const docxComponents = {
    // Create header exactly matching PDF - REMOVED ALL BORDERS
    createHeader: async () => {
        const headerParagraphs = [];

        const headerTable = new Table({
            rows: [
                new TableRow({
                    children: [
                        // Logo cell (20%)
                        new TableCell({
                            children: logoBase64docx ? [
                                new Paragraph({
                                    children: [await docxUtils.base64ToImageRun(logoBase64docx, 100, 100)],
                                    alignment: AlignmentType.LEFT
                                })

                            ] : [docxUtils.createParagraph("")],
                            width: { size: 20, type: WidthType.PERCENTAGE },
                            verticalAlign: "center",
                            borders: {
                                top: { style: "nil" },
                                bottom: { style: "nil" },
                                left: { style: "nil" },
                                right: { style: "nil" }
                            }
                        }),
                        // Title cell (60%)
                        new TableCell({
                            children: [
                                docxUtils.createParagraph("INSPECTION REPORT", {
                                    bold: true,
                                    size: docxDesignSystem.sizes.title * 2,
                                    color: docxDesignSystem.colors.gray.dark,
                                    alignment: AlignmentType.CENTER,
                                    font: docxDesignSystem.fonts.heading,
                                    spaceAfter: 100
                                }),
                                docxUtils.createParagraph("Professional Maritime Inspection Documentation", {
                                    italic: true,
                                    size: docxDesignSystem.sizes.body * 2,
                                    color: docxDesignSystem.colors.gray.medium,
                                    alignment: AlignmentType.CENTER,
                                    spaceAfter: 0
                                })
                            ],
                            width: { size: 60, type: WidthType.PERCENTAGE },
                            verticalAlign: "center",
                            borders: {
                                top: { style: "nil" },
                                bottom: { style: "nil" },
                                left: { style: "nil" },
                                right: { style: "nil" }
                            }
                        }),
                        // Date/IMO cell (20%)
                        new TableCell({
                            children: [
                                docxUtils.createParagraph("Date & Time: 10-09-2024 11:00", {
                                    size: docxDesignSystem.sizes.body * 2,
                                    alignment: AlignmentType.RIGHT,
                                    spaceAfter: 100
                                }),
                                docxUtils.createParagraph("IMO: 22222", {
                                    size: docxDesignSystem.sizes.body * 2,
                                    alignment: AlignmentType.RIGHT,
                                    spaceAfter: 0
                                })
                            ],
                            width: { size: 20, type: WidthType.PERCENTAGE },
                            verticalAlign: "center",
                            borders: {
                                top: { style: "nil" },
                                bottom: { style: "nil" },
                                left: { style: "nil" },
                                right: { style: "nil" }
                            }
                        })
                    ]
                })
            ],
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: {
                top: { style: "nil" },
                bottom: { style: "nil" },
                left: { style: "nil" },
                right: { style: "nil" },
                insideHorizontal: { style: "nil" },
                insideVertical: { style: "nil" }
            }
        });

        headerParagraphs.push(headerTable);
        headerParagraphs.push(docxUtils.createParagraph("", { spaceAfter: 600 }));

        return headerParagraphs;
    },

    // Create module section (Step1, Step2, etc.)
    createModuleSection: async (step) => {
        const elements = [];

        // Module title with primary color
        elements.push(
            docxUtils.createParagraph(step.step_name || "Step", {
                bold: true,
                size: docxDesignSystem.sizes.heading1 * 2,
                color: docxDesignSystem.colors.primary,
                heading: HeadingLevel.HEADING_1,
                spaceAfter: 200
            })
        );

        // Module guidance in italics
        if (step.guidance) {
            elements.push(
                docxUtils.createParagraph(`Guidance: ${step.guidance}`, {
                    italic: true,
                    size: docxDesignSystem.sizes.body * 2,
                    color: docxDesignSystem.colors.gray.medium,
                    spaceAfter: 400
                })
            );
        }

        return elements;
    },

    // Create side-by-side layout matching PDF exactly - REMOVED BORDERS
    createSideBySideLayout: async (images, formData) => {
        const elements = [];

        // Create images content
        const imageElements = await docxComponents.createImagesGrid(images);

        // Create data content
        const dataElements = docxComponents.createDataSection(formData);

        // Create side-by-side table (52% images, 48% data) - EXPLICITLY REMOVE ALL BORDERS
        const layoutTable = new Table({
            rows: [
                new TableRow({
                    children: [
                        // Images column (left - 52%)
                        new TableCell({
                            children: imageElements,
                            width: { size: 52, type: WidthType.PERCENTAGE },
                            verticalAlign: "top",
                            margins: {
                                top: 100,
                                bottom: 100,
                                left: 100,
                                right: 200
                            },
                            borders: {
                                top: { style: "nil" },
                                bottom: { style: "nil" },
                                left: { style: "nil" },
                                right: { style: "nil" }
                            }
                        }),
                        // Data column (right - 48%)
                        new TableCell({
                            children: dataElements,
                            width: { size: 48, type: WidthType.PERCENTAGE },
                            verticalAlign: "top",
                            margins: {
                                top: 100,
                                bottom: 100,
                                left: 200,
                                right: 100
                            },
                            borders: {
                                top: { style: "nil" },
                                bottom: { style: "nil" },
                                left: { style: "nil" },
                                right: { style: "nil" }
                            }
                        })
                    ]
                })
            ],
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: {
                top: { style: "nil" },
                bottom: { style: "nil" },
                left: { style: "nil" },
                right: { style: "nil" },
                insideHorizontal: { style: "nil" },
                insideVertical: { style: "nil" }
            }
        });

        elements.push(layoutTable);
        return elements;
    },

    // Create images grid (2x2 layout like PDF) - REMOVED BORDERS
    createImagesGrid: async (images) => {
        const elements = [];

        if (!images || images.length === 0) return elements;

        const imagesPerRow = 2;
        const rows = [];

        for (let i = 0; i < images.length; i += imagesPerRow) {
            const rowImages = images.slice(i, i + imagesPerRow);
            const cells = [];

            for (let j = 0; j < imagesPerRow; j++) {
                const image = rowImages[j];

                if (image && image.image_url) {
                    const cellChildren = [];

                    try {
                        const imageRun = await docxUtils.base64ToImageRun(image.image_url, 100, 100);
                        if (imageRun) {
                            cellChildren.push(
                                new Paragraph({
                                    children: [imageRun],
                                    alignment: AlignmentType.CENTER,
                                    spacing: { after: 100 }
                                })
                            );
                        }

                        // Add image name below
                        const imageName = image.component_name || `Image ${i + j + 1}`;
                        const truncatedName = imageName.length > 25 ? imageName.substring(0, 22) + '...' : imageName;
                        cellChildren.push(
                            docxUtils.createParagraph(truncatedName, {
                                size: docxDesignSystem.sizes.caption * 2,
                                alignment: AlignmentType.CENTER,
                                color: docxDesignSystem.colors.gray.dark,
                                spaceAfter: 0
                            })
                        );
                    } catch (error) {
                        console.error('Error adding image:', error);
                        cellChildren.push(
                            docxUtils.createParagraph("[Image Unavailable]", {
                                italic: true,
                                color: docxDesignSystem.colors.gray.medium,
                                alignment: AlignmentType.CENTER
                            })
                        );
                    }

                    cells.push(new TableCell({
                        children: cellChildren,
                        width: { size: 50, type: WidthType.PERCENTAGE },
                        borders: {
                            top: { style: "nil" },
                            bottom: { style: "nil" },
                            left: { style: "nil" },
                            right: { style: "nil" }
                        }
                    }));
                } else {
                    // Empty cell
                    cells.push(new TableCell({
                        children: [docxUtils.createParagraph("")],
                        width: { size: 50, type: WidthType.PERCENTAGE },
                        borders: {
                            top: { style: "nil" },
                            bottom: { style: "nil" },
                            left: { style: "nil" },
                            right: { style: "nil" }
                        }
                    }));
                }
            }

            rows.push(new TableRow({ children: cells }));
        }

        if (rows.length > 0) {
            const imageTable = new Table({
                rows: rows,
                width: { size: 100, type: WidthType.PERCENTAGE },
                borders: {
                    top: { style: "nil" },
                    bottom: { style: "nil" },
                    left: { style: "nil" },
                    right: { style: "nil" },
                    insideHorizontal: { style: "nil" },
                    insideVertical: { style: "nil" }
                }
            });

            elements.push(imageTable);
        }

        return elements;
    },

    // Create data section matching PDF exactly - REMOVED BORDERS
    createDataSection: (formData) => {
        const elements = [];

        if (!formData || Object.keys(formData).length === 0) return elements;

        // Data header with background
        elements.push(
            new Paragraph({
                children: [
                    new TextRun({
                        text: "Data",
                        bold: true,
                        size: docxDesignSystem.sizes.heading3 * 2,
                        color: docxDesignSystem.colors.primaryDark
                    })
                ],
                shading: {
                    fill: "E6F7F1" // Light green background like PDF
                },
                spacing: { before: 100, after: 300 }
            })
        );

        const formEntries = Object.entries(formData)
            .filter(([key, value]) => value != null && value.toString().trim() !== '');

        // Field descriptions
        const fieldDescriptions = {
            'test1': 'Primary measurement value for equipment performance assessment',
            'remarktest': 'Additional observations and comments from the inspection',
            'remark1': 'Initial inspection remarks and observations'
        };

        // Create data entries - REMOVED BORDERS
        formEntries.forEach(([key, value]) => {
            const formattedKey = key.replace(/_/g, ' ')
                .replace(/([A-Z])/g, ' $1')
                .replace(/^\w/, c => c.toUpperCase())
                .trim();

            // Key-value pair with background - EXPLICITLY REMOVE ALL BORDERS
            const dataTable = new Table({
                rows: [
                    new TableRow({
                        children: [
                            new TableCell({
                                children: [
                                    docxUtils.createParagraph(formattedKey + ':', {
                                        bold: true,
                                        size: docxDesignSystem.sizes.body * 2,
                                        color: docxDesignSystem.colors.gray.dark,
                                        spaceAfter: 0
                                    })
                                ],
                                width: { size: 35, type: WidthType.PERCENTAGE },
                                shading: { fill: "F9FAFB" },
                                borders: {
                                    top: { style: "nil" },
                                    bottom: { style: "nil" },
                                    left: { style: "nil" },
                                    right: { style: "nil" }
                                }
                            }),
                            new TableCell({
                                children: [
                                    docxUtils.createParagraph(value.toString(), {
                                        size: docxDesignSystem.sizes.body * 2,
                                        color: docxDesignSystem.colors.primary,
                                        bold: false,
                                        spaceAfter: 0
                                    })
                                ],
                                width: { size: 65, type: WidthType.PERCENTAGE },
                                shading: { fill: "F9FAFB" },
                                borders: {
                                    top: { style: "nil" },
                                    bottom: { style: "nil" },
                                    left: { style: "nil" },
                                    right: { style: "nil" }
                                }
                            })
                        ]
                    })
                ],
                width: { size: 100, type: WidthType.PERCENTAGE },
                borders: {
                    top: { style: "nil" },
                    bottom: { style: "nil" },
                    left: { style: "nil" },
                    right: { style: "nil" },
                    insideHorizontal: { style: "nil" },
                    insideVertical: { style: "nil" }
                }
            });

            elements.push(dataTable);

            // Add field description note if available
            const fieldDescription = fieldDescriptions[key] || fieldDescriptions[key.toLowerCase()];
            if (fieldDescription) {
                elements.push(
                    docxUtils.createParagraph(`Note: ${fieldDescription}`, {
                        italic: true,
                        size: (docxDesignSystem.sizes.caption + 1) * 2,
                        color: docxDesignSystem.colors.gray.medium,
                        spaceAfter: 200
                    })
                );
            }
        });

        return elements;
    },

    // Create remarks section matching PDF - REMOVED BORDERS
    createRemarksSection: (commonData) => {
        if (!commonData || !commonData.form_data || Object.keys(commonData.form_data).length === 0) {
            return [];
        }

        const commonEntries = Object.entries(commonData.form_data)
            .filter(([key, value]) => value != null && value.toString().trim() !== '');

        if (commonEntries.length === 0) {
            return [];
        }

        const elements = [];

        // Remarks header with background
        elements.push(
            new Paragraph({
                children: [
                    new TextRun({
                        text: "Remarks",
                        bold: true,
                        size: docxDesignSystem.sizes.heading3 * 2,
                        color: docxDesignSystem.colors.primaryDark
                    })
                ],
                shading: {
                    fill: "E6F7F1" // Light green background like PDF
                },
                spacing: { before: 400, after: 300 }
            })
        );

        // Field descriptions for remarks
        const fieldDescriptions = {
            'remark1': 'Initial inspection remarks and observations',
            'remarktest': 'Additional observations and comments from the inspection'
        };

        // Create remarks entries - REMOVED BORDERS
        commonEntries.forEach(([key, value]) => {
            const formattedKey = key.replace(/_/g, ' ')
                .replace(/([A-Z])/g, ' $1')
                .replace(/^\w/, c => c.toUpperCase())
                .trim();

            const remarkTable = new Table({
                rows: [
                    new TableRow({
                        children: [
                            new TableCell({
                                children: [
                                    docxUtils.createParagraph(formattedKey + ':', {
                                        bold: true,
                                        size: docxDesignSystem.sizes.body * 2,
                                        color: docxDesignSystem.colors.gray.dark,
                                        spaceAfter: 0
                                    })
                                ],
                                width: { size: 25, type: WidthType.PERCENTAGE },
                                shading: { fill: "F9FAFB" },
                                borders: {
                                    top: { style: "nil" },
                                    bottom: { style: "nil" },
                                    left: { style: "nil" },
                                    right: { style: "nil" }
                                }
                            }),
                            new TableCell({
                                children: [
                                    docxUtils.createParagraph(value.toString(), {
                                        size: docxDesignSystem.sizes.body * 2,
                                        color: docxDesignSystem.colors.primary,
                                        spaceAfter: 0
                                    })
                                ],
                                width: { size: 75, type: WidthType.PERCENTAGE },
                                shading: { fill: "F9FAFB" },
                                borders: {
                                    top: { style: "nil" },
                                    bottom: { style: "nil" },
                                    left: { style: "nil" },
                                    right: { style: "nil" }
                                }
                            })
                        ]
                    })
                ],
                width: { size: 100, type: WidthType.PERCENTAGE },
                borders: {
                    top: { style: "nil" },
                    bottom: { style: "nil" },
                    left: { style: "nil" },
                    right: { style: "nil" },
                    insideHorizontal: { style: "nil" },
                    insideVertical: { style: "nil" }
                }
            });

            elements.push(remarkTable);

            // Add field descriptions
            const fieldDescription = fieldDescriptions[key] || fieldDescriptions[key.toLowerCase()];
            if (fieldDescription) {
                elements.push(
                    docxUtils.createParagraph(`Note: ${fieldDescription}`, {
                        italic: true,
                        size: (docxDesignSystem.sizes.caption + 1) * 2,
                        color: docxDesignSystem.colors.gray.medium,
                        spaceAfter: 200
                    })
                );
            }
        });

        return elements;
    }
};

// DOCX Utilities
const docxUtils = {
    createParagraph: (text, options = {}) => {
        return new Paragraph({
            children: [
                new TextRun({
                    text: text,
                    bold: options.bold || false,
                    italics: options.italic || false,
                    color: options.color || "000000",
                    size: options.size || docxDesignSystem.sizes.body * 2, // Word uses half-points
                    font: options.font || docxDesignSystem.fonts.primary
                })
            ],
            alignment: options.alignment || AlignmentType.LEFT,
            spacing: {
                before: options.spaceBefore || 0,
                after: options.spaceAfter || 200
            },
            heading: options.heading
        });
    },

    base64ToImageRun: async (base64String, width = 200, height = 200) => {
        try {
            // Remove data URL prefix if present
            const base64Data = base64String.replace(/^data:image\/[a-z]+;base64,/, '');

            return new ImageRun({
                data: Buffer.from(base64Data, 'base64'),
                transformation: {
                    width: width,
                    height: height
                }
            });
        } catch (error) {
            console.error('Error creating image:', error);
            return null;
        }
    }
};

// DOCX Design System
const docxDesignSystem = {
    colors: {
        primary: "238A70",
        primaryDark: "124536",
        white: "FFFFFF",
        gray: {
            light: "F3F4F6",
            medium: "6B7280",
            dark: "374151"
        }
    },
    sizes: {
        caption: 8,
        body: 10,
        heading3: 12,
        heading2: 14,
        heading1: 16,
        title: 18
    },
    fonts: {
        primary: "Calibri",
        heading: "Calibri"
    }
};
export const exportInspectionReport = async (inspection, shipinfo, userinfo, formconfiguration, format = 'pdf') => {
    if (!inspection) {
        toast.error('No inspection data provided');
        return;
    }
     message.loading('Generating report...', 1);

    try {
        switch (format.toLowerCase()) {
            case 'pdf':
                await exportToPDF(inspection, shipinfo, userinfo, formconfiguration);
                break;
            case 'docx':
            case 'doc':
                await exportToDOCX(inspection);
                break;
            default:
                toast.error('Unsupported export format. Please choose PDF or DOCX.');
                break;
        }
    } catch (error) {
        console.error('Export error:', error);
        toast.error('Failed to export report: ' + error.message);
    }
};

// Export format options for dropdown
export const exportFormatOptions = [
    {
        value: 'pdf',
        label: 'PDF Document',
        description: 'Portable Document Format - Best for viewing and printing'
    },
    {
        value: 'docx',
        label: 'Word Document',
        description: 'Microsoft Word format - Best for editing and collaboration'
    }
];
